"use client";
import React, { useRef, useEffect } from "react";

const services = [
  {
    icon: "🔮",
    title: "Tarot Reading",
    desc: "Clarity for love, career, and life decisions.",
  },
  {
    icon: "🔢",
    title: "Numerology Guidance",
    desc: "Understand your life path and hidden patterns.",
  },
  {
    icon: "✨",
    title: "Energy Block Clearing",
    desc: "Remove emotional and spiritual blockages.",
  },
  {
    icon: "🧠",
    title: "Subconscious Reprogramming",
    desc: "Transform your mindset and attract better outcomes.",
  },
];

const ServicesSection: React.FC = () => {
  const sectionRef = useRef<HTMLElement>(null);
  const cardsRef = useRef<(HTMLDivElement | null)[]>([]);
  const parallaxRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    /* Card reveal on scroll */
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.remove("card-hidden");
            entry.target.classList.add("card-reveal");
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 }
    );

    cardsRef.current.forEach((card) => {
      if (card) {
        card.classList.add("card-hidden");
        observer.observe(card);
      }
    });

    /* Parallax scroll effect */
    let rafId: number;
    const handleParallax = () => {
      if (!parallaxRef.current) return;
      const rect = parallaxRef.current.getBoundingClientRect();
      const speed = 0.6;
      const offset = rect.top * (1 - speed);
      parallaxRef.current.style.transform = `translateY(${offset * 0.08}px)`;
      rafId = requestAnimationFrame(handleParallax);
    };
    rafId = requestAnimationFrame(handleParallax);

    return () => {
      observer.disconnect();
      cancelAnimationFrame(rafId);
    };
  }, []);

  return (
    <section id="services" className="relative py-20 md:py-28 px-6">
      <div ref={parallaxRef} className="max-w-6xl mx-auto" style={{ willChange: "transform" }}>
        <div className="text-center mb-14">
          <span className="section-label">What I Offer</span>
          <h2 className="font-display text-3xl sm:text-4xl md:text-5xl text-foreground mt-4">
            Services
          </h2>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {services.map((s, i) => (
            <div
              key={i}
              ref={(el) => { cardsRef.current[i] = el; }}
              className="service-card p-8 flex flex-col items-center text-center gap-4"
            >
              <span className="text-4xl">{s.icon}</span>
              <h3 className="font-display text-xl text-foreground">{s.title}</h3>
              <p className="text-foreground-muted text-sm font-body leading-relaxed">
                {s.desc}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ServicesSection;
